import React from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  QUERY_KEYS,
  CACHE_TIMES,
  METADATA_FIELDS,
  METADATA_FIELD_TOOLTIP_DESCRIPTIONS,
  MetadataFieldKey,
} from './constants';
import { WebsiteViewService } from 'src/model/WebsiteViewService';
import { toNumber } from 'src/utils/string-helper';
import { UrlHelper } from '../../utils/url-helper';
import { Badge, BadgeVariant } from '../shared-panels/Badge';
import { CountValue } from '../shared-panels/CountValue';
import '../shared-panels/panel-tokens.scss';
import './website-header.scss';

interface HeaderField {
  key: MetadataFieldKey;
  value: React.ReactNode;
  /** Ids are read character by character and compared, so they are set in a monospace face. */
  mono?: boolean;
  /** True when the response did not carry the field, so it renders as a dash rather than a value. */
  absent?: boolean;
}

/** Renders as the placeholder the metadata card uses for a field the response did not carry. */
const ABSENT = '—';

/**
 * The status the backend reported, as a badge — or nothing at all when it reported none.
 *
 * Only an exact `active` is coloured as healthy. `websiteStatus` is a free-form string on the wire with no
 * enumerated vocabulary, so any broader mapping would be guesswork, and guessing wrong here means a green
 * badge over a website that is not in fact running. Everything else is shown verbatim in a neutral badge:
 * the reader still learns the state, without the colour asserting something the payload does not say.
 */
const statusVariant = (status: string): BadgeVariant =>
  status.trim().toLowerCase() === 'active' ? 'success' : 'neutral';

/**
 * The identity of the website whose numbers the panels below are showing: domain and status, then every
 * metadata field the page holds — which website, under which program, on which platform, how large.
 *
 * This carries the full field set of the `WebsiteMetadata` card deliberately. The card spends a full-width
 * panel and a twelve-cell grid on eight short strings that never change while you are reading the page;
 * the intent is that it goes away, and nothing goes with it. So this is a superset: the same eight fields
 * in the same order, under the same labels, with the same provenance in their tooltips, plus the status
 * the card holds in its payload but never renders.
 *
 * Renders nothing at all when no website is selected. An empty heading above the funnel would imply a
 * website had been chosen and its name lost, which is worse than the search bar's own empty state.
 */
export const WebsiteHeader = React.memo((): JSX.Element | null => {
  // Subscribes this component to route changes, as every sibling in this directory does. Load-bearing
  // rather than idiomatic: the website lives in the query string, which `UrlHelper` reads outside React,
  // so `memo` sees identical props across a `history.push` from the selector and skips the re-render. The
  // metadata card beside it does subscribe, so without this the two would disagree — this header naming
  // the previous website while the card below it names the new one. One wrong identity is recoverable;
  // two contradictory ones on the same screen leave the reader unable to trust either.
  useLocation();
  const website = UrlHelper.getParamValue('website');
  const websiteId = toNumber(UrlHelper.getParamValue('websiteId'));

  // Shares the metadata card's query key and arguments, so the two resolve from one request rather than
  // both calling the backend for the same website.
  const { data: metadata, isFetching: isLoading } = useQuery({
    queryKey: [QUERY_KEYS.WEBSITE_METADATA, website, websiteId],
    queryFn: () => new WebsiteViewService().getWebsiteMetadata(website || '', websiteId),
    enabled: !!website || !!websiteId,
    staleTime: CACHE_TIMES.STALE_TIME,
    cacheTime: CACHE_TIMES.CACHE_TIME,
    retry: 3,
  });

  if (!website && !websiteId) {
    return null;
  }

  /**
   * A field the response did not carry, shown as a dash rather than dropped.
   *
   * The opposite of what a page header would normally do, and deliberate: this stands in for the metadata
   * card, and the card accounts for all eight fields whether or not they arrived. Dropping them instead
   * would leave a reader who finds no platform unable to tell an unknown platform from one this line does
   * not report, and would make the field order shift from website to website — which is what stops the
   * line being scannable once you have read it twice.
   */
  const field = (
    key: MetadataFieldKey,
    value: string | number | null | undefined,
    mono = false
  ): HeaderField =>
    value === undefined || value === null || value === ''
      ? { key, value: ABSENT, absent: true }
      : { key, value: String(value), mono };

  /** A list-valued field, joined into one entry rather than repeating its label per value. */
  const listField = (key: MetadataFieldKey, values: string[] | undefined): HeaderField =>
    field(key, values?.length ? values.join(', ') : null);

  /**
   * The selection size compacted, with the exact figure in its tooltip — the one field here whose value is
   * a magnitude rather than a name, and the only one long enough to push the rest of the line off screen.
   *
   * Guarded on finiteness because `CountValue` throws on a non-numeric count by design, and this header
   * sits outside the funnel's ErrorBoundary: a malformed size would otherwise take the whole caption down
   * along with the seven fields that were fine.
   */
  const sizeField = (size: number | undefined): HeaderField =>
    typeof size === 'number' && Number.isFinite(size)
      ? { key: MetadataFieldKey.SELECTION_SIZE, value: <CountValue count={size} /> }
      : { key: MetadataFieldKey.SELECTION_SIZE, value: ABSENT, absent: true };

  // Same order as the metadata card's grid, so a reader moving from that card to this line finds the
  // fields where they left them.
  const fields: HeaderField[] = metadata
    ? [
        field(MetadataFieldKey.WEBSITE_ID, metadata.websiteId, true),
        field(MetadataFieldKey.WEBSITE_TYPE, metadata.websiteType),
        listField(MetadataFieldKey.PROGRAM, metadata.programs),
        listField(MetadataFieldKey.SUB_PROGRAM, metadata.subPrograms),
        sizeField(metadata.estSelectionSize),
        field(MetadataFieldKey.PLATFORM, metadata.platform),
        listField(MetadataFieldKey.OPERATING_COUNTRIES, metadata.operatingCountries),
        field(MetadataFieldKey.COUNTRY_OF_ORIGIN, metadata.countryOfOrigin),
      ]
    : [];

  // A response carrying nothing is not worth eight dashes: it is the same state as a failed one, and is
  // reported as such below.
  const hasAnyField = fields.some((entry) => !entry.absent);

  return (
    <div className="ovp-website-header">
      <div className="ovp-website-header__head">
        {/* The domain from the URL, not from the response: it is what the user searched for, so it is
            correct to show it immediately rather than after the metadata call resolves. Falls back to the
            id for the id-only search, which is a real way to reach this page. */}
        <h1 className="ovp-website-header__domain">{website || `Website ${websiteId}`}</h1>
        {metadata?.websiteStatus && (
          <Badge variant={statusVariant(metadata.websiteStatus)}>{metadata.websiteStatus}</Badge>
        )}
      </div>

      {isLoading ? (
        <div className="ovp-website-header__note">Loading website metadata...</div>
      ) : hasAnyField ? (
        <div className="ovp-website-header__meta">
          {fields.map((entry, index) => (
            <span key={entry.key} className="ovp-website-header__field">
              {/* Before rather than after the field, so the last field never trails a separator. */}
              {index > 0 && <span className="ovp-website-header__sep">·</span>}
              {/* Where the field comes from, carried over from the card's own tooltips: "Program" is
                  sourced from the program source repository while the rest come from EWC, and that is
                  the first thing asked when a value looks wrong. */}
              <span
                className="ovp-website-header__label"
                title={METADATA_FIELD_TOOLTIP_DESCRIPTIONS[entry.key]}
              >
                {METADATA_FIELDS[entry.key]}
              </span>
              <span
                className={[
                  'ovp-website-header__value',
                  entry.mono ? 'ovp-website-header__value--mono' : '',
                  entry.absent ? 'ovp-website-header__value--absent' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {entry.value}
              </span>
            </span>
          ))}
        </div>
      ) : (
        // Both the error and the empty-response case. Stated plainly: the panels below are still worth
        // reading, and saying "failed" about a caption would overstate what has gone wrong.
        <div className="ovp-website-header__note">Website metadata unavailable.</div>
      )}
    </div>
  );
});

WebsiteHeader.displayName = 'WebsiteHeader';
